import isEqual from 'lodash/isEqual';
import { BoardModel } from '../../game/boardMVC/models/board/BoardModel';
import type { BoardState, GridPosition } from '../../game/boardMVC/types';
import { removeRandomElement } from '../../utils/array-utils';
import type { LevelData } from '../../game/level/LevelData';
import { waitForMs } from '../../utils/async-utils';
import type { BoardConnectivity } from '../../game/boardMVC/utils/getBoardConnectivity';
import { getValidGridPositions } from '../../game/boardMVC/utils/getValidGridPositions';
import { getProperConnectivity } from '../../game/boardMVC/utils/getBoardConnectivity';
import { applyHorizontalSymmetry } from './utils/applyHorizontalSymmetry';
import { applyVerticalSymmetry } from './utils/applyVerticalSymmetry';
import { getRandomStackColors } from './utils/getRandomStackColors';
import { extractBoardStateColors } from '../../game/boardMVC/utils/extractBoardStateColors';
import { boardsHaveSimilarBlockers } from './utils/boardsHaveSimilarBlockers';
import { sortGridPositions } from '../../game/boardMVC/utils/sortGridPositions';
import type { RemixLevelConfig, RemixLevelParameters, RemixSizeConfig, RemixColorsConfig } from './types';
import { applyDiagonalSymmetry } from './utils/applyDiagonalSymmetry';

interface RemixResult {
  readonly board: BoardState;
  readonly sizeRatio: number;
}

export const DEFAULT_REMIX_PARAMETERS: RemixLevelParameters = {
  size: {
    deltaCols: 0,
    deltaRows: 0,
  },
  thresholds: {
    connectivity: 10,
    stacks: 1,
    openSpaces: 0,
    blockers: 2,
  },
  colors: {
    minColorRepeat: 2,
    maxColorRepeat: 5,
  },
  symmetry: {
    horizontal: false,
    vertical: false,
    diagonal: false,
  },
};

function generateRemixedBoardState(
  originalState: BoardState,
  size: RemixSizeConfig,
  colors?: RemixColorsConfig,
): RemixResult {
  const remixedState: BoardState = {
    cols: originalState.cols,
    rows: originalState.rows,
    stacks: [],
    hiddenStacks: [],
    blockers: [],
  };

  remixedState.cols += size.deltaCols;
  remixedState.rows += size.deltaRows;

  const originalColors = extractBoardStateColors(originalState);
  const sizeRatio = (remixedState.cols * remixedState.rows) / (originalState.cols * originalState.rows);

  // Add excluded / hidden stacks to the remixed state.
  const boardXYs = getValidGridPositions(remixedState);

  while (
    boardXYs.length > 0 &&
    remixedState.hiddenStacks.length < Math.ceil(originalState.hiddenStacks.length * sizeRatio)
  ) {
    const { x, y } = removeRandomElement(boardXYs);
    remixedState.hiddenStacks.push({ x, y });
  }

  const availableStackXYs = boardXYs.slice();
  const filledStackXYs: GridPosition[] = [];

  // Add defined stacks to the remixed state.
  originalState.stacks.forEach((stack) => {
    const randomXY = removeRandomElement(availableStackXYs);
    const tiles = colors
      ? getRandomStackColors(originalColors, stack.tiles.length, colors.minColorRepeat, colors.maxColorRepeat)
      : stack.tiles.slice();
    remixedState.stacks.push({ x: randomXY.x, y: randomXY.y, tiles });
    filledStackXYs.push(randomXY);
  });

  // Add blockers to the remixed state. If a blocker was on top of tiles it should remain so.
  if (originalState.blockers) {
    originalState.blockers.forEach((blocker) => {
      const stackHasTiles = originalState.stacks.some((stack) => blocker.x === stack.x && blocker.y === stack.y);
      const { x, y } = removeRandomElement(!stackHasTiles ? availableStackXYs : filledStackXYs);
      remixedState.blockers?.push({ ...blocker, x, y });
    });
  }

  return {
    board: remixedState,
    sizeRatio,
  };
}

export async function generateRemixedLevel(config: RemixLevelConfig): Promise<LevelData | null> {
  console.log('level remix started');

  const { originalLevel, timeoutMs, thresholds } = config;
  const originalModel = new BoardModel({ state: originalLevel.board });
  const originalConnectivity = getProperConnectivity(originalModel);

  const startTime = Date.now();
  const remixedLevel: LevelData = {
    ...originalLevel,
  };

  let attempts = 0;
  let remixModel: BoardModel;
  let remixResult: RemixResult;

  do {
    if (config.cancelled) {
      console.log(`level remix cancelled`);
      return null;
    }

    if (Date.now() > startTime + timeoutMs) {
      console.log(`level remix timed out`);
      return null;
    }

    // to not lock process.
    if (attempts % 500 === 0) {
      await waitForMs(0);
    }

    remixResult = generateRemixedBoardState(originalLevel.board, config.size, config.colors);
    remixedLevel.board = remixResult.board;

    if (config.symmetry.horizontal) {
      remixedLevel.board = applyHorizontalSymmetry(remixResult.board);
    }

    if (config.symmetry.vertical) {
      remixedLevel.board = applyVerticalSymmetry(remixedLevel.board);
    }

    if (config.symmetry.diagonal) {
      remixedLevel.board = applyDiagonalSymmetry(remixedLevel.board);
    }

    attempts += 1;

    remixModel = new BoardModel({
      state: remixedLevel.board,
    });
  } while (
    !hasEquivalentConnectivity(
      originalConnectivity,
      getProperConnectivity(remixModel),
      remixResult.sizeRatio,
      thresholds.connectivity,
    ) ||
    remixModel.hasOrphanedStacks() ||
    !hasSameTotalColorCount(originalLevel.board, remixedLevel.board) ||
    !boardsHaveSimilarBlockers(originalLevel.board, remixedLevel.board, thresholds.blockers) ||
    !hasApproximateStacks(originalLevel.board, remixedLevel.board, thresholds.stacks) ||
    !hasApproximateOpenSpaces(originalModel, remixModel, thresholds.openSpaces) ||
    isSameLayout(originalLevel.board, remixedLevel.board)
  );

  console.log(`level remix  success attempts: ${attempts}`);
  return remixedLevel;
}

function hasEquivalentConnectivity(
  a: BoardConnectivity,
  b: BoardConnectivity,
  sizeRatio: number,
  threshold: number,
): boolean {
  return Math.abs(b.max - a.max * sizeRatio) <= threshold && Math.abs(b.current - a.current * sizeRatio) <= threshold;
}

function hasSameTotalColorCount(a: BoardState, b: BoardState): boolean {
  return extractBoardStateColors(a).length === extractBoardStateColors(b).length;
}

function hasApproximateStacks(a: BoardState, b: BoardState, threshold: number): boolean {
  return Math.abs(a.stacks.length - b.stacks.length) <= threshold;
}

function hasApproximateOpenSpaces(a: BoardModel, b: BoardModel, threshold: number): boolean {
  return Math.abs(a.getEmptyStackCount() - b.getEmptyStackCount()) <= threshold;
}

function isSameLayout(stateA: BoardState, stateB: BoardState): boolean {
  return (
    stateA.cols === stateB.cols &&
    stateA.rows === stateB.rows &&
    isEqual(sortGridPositions(stateA.stacks), sortGridPositions(stateB.stacks)) &&
    isEqual(sortGridPositions(stateA.hiddenStacks), sortGridPositions(stateB.hiddenStacks)) &&
    isEqual(sortGridPositions(stateA.blockers ?? []), sortGridPositions(stateB.blockers ?? []))
  );
}
